import { useEffect, useMemo, useState } from 'react';
import { useList, useOne } from '@refinedev/core';
import { Check, ChevronsUpDown, Plus, Trash2, Copy, Loader2 } from 'lucide-react';
import { notify } from '@/lib/notify';
import { cn } from '@/lib/utils';
import { apiClient } from '@/providers/api-client';
import { SessionSelector } from '@/components/shared/SessionSelector';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PricingTierInfoContent } from '@/components/shared/PricingTierBadge';
import {
  formatCurrencyPrice,
  formatDuration,
  type PricingTier,
} from '@/components/shared/PricingTiersTooltip';
import type { Order } from '../types';

/** Каналы доставки инвойса. Зеркало backend delivery enum. */
const INVOICE_DELIVERIES = ['copy', 'telegram', 'email', 'telegram_session'] as const;
type InvoiceDelivery = (typeof INVOICE_DELIVERIES)[number];

const isInvoiceDelivery = (v: string): v is InvoiceDelivery =>
  v === 'copy' || v === 'telegram' || v === 'email' || v === 'telegram_session';

/**
 * Типы товаров, которые поддерживает backend createOrderSchema.
 * tierFilter — имя query-параметра фильтра PricingTier по этому товару
 * (GET /pricing-tiers/admin?<tierFilter>=<targetId>).
 */
const TARGET_TYPES = [
  { value: 'COURSE', label: 'Курс', resource: 'courses', tierFilter: 'courseId' },
  { value: 'CHANNEL_PASS', label: 'Доступ к каналу', resource: 'channel-passes', tierFilter: 'channelPassId' },
  { value: 'SUBSCRIPTION_PLAN', label: 'Абонемент', resource: 'subscription-plans', tierFilter: 'subscriptionPlanId' },
  { value: 'BOOK', label: 'Книга', resource: 'books', tierFilter: 'bookId' },
] as const;

type TargetTypeValue = (typeof TARGET_TYPES)[number]['value'];

interface DraftItem {
  key: string;
  targetType: TargetTypeValue;
  targetId: number | null;
  pricingTierId: number | null;
  quantity: number;
}

/** Каноническое имя тарифа (паттерн PricingTierBadge.getTierName). */
function tierName(tier: PricingTier, index: number): string {
  return (
    tier.name ||
    tier.translations?.ru?.name ||
    `Тариф ${index + 1}`
  );
}

/**
 * Текстовый лейбл тарифа для пункта Select (radix SelectItem требует
 * простой контент — tooltip-инфа показывается отдельным PricingTierBadge).
 */
function tierLabel(tier: PricingTier, index: number): string {
  const name = tierName(tier, index);
  const price = tier.prices[tier.baseCurrency];
  const pricePart =
    typeof price === 'number' && price > 0
      ? ` — ${formatCurrencyPrice(price, tier.baseCurrency)}`
      : ' — бесплатно';
  const durationPart = ` · ${formatDuration(tier.durationDays ?? null)}`;
  return `${name}${pricePart}${durationPart}`;
}

interface MinimalUser {
  id: number;
  firstName: string | null;
  lastName: string | null;
  username: string | null;
  email: string | null;
  telegramId: string | null;
}

/**
 * Перевод сущности. В admin два формата list-ответов:
 *  - массив `{ languageCode, title|name }[]` — courses, books
 *  - Record `{ ru: { name|title }, ... }` — subscription-plans, channel-passes
 * COURSE/BOOK используют поле `title`, CHANNEL_PASS/SUBSCRIPTION_PLAN — `name`.
 */
interface TranslationFields {
  title?: string;
  name?: string;
}

interface EntityTranslationItem extends TranslationFields {
  languageCode: string;
}

interface MinimalEntity {
  id: number;
  slug?: string;
  code?: string;
  title?: string;
  name?: string;
  translations?: EntityTranslationItem[] | Record<string, TranslationFields>;
}

interface CreateOrderInvoiceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Вызывается после успешного создания заказа — для рефреша списка. */
  onCreated?: () => void;
}

function userLabel(u: MinimalUser): string {
  const name = [u.firstName, u.lastName].filter(Boolean).join(' ');
  return name || u.username || u.email || `user#${u.id}`;
}

function translationText(t: TranslationFields | undefined): string | undefined {
  if (!t) { return undefined; }
  if (typeof t.title === 'string' && t.title.length > 0) { return t.title; }
  if (typeof t.name === 'string' && t.name.length > 0) { return t.name; }
  return undefined;
}

/** Достаёт название из translations любого из двух admin-форматов. */
function textFromTranslations(
  translations: MinimalEntity['translations'],
): string | undefined {
  if (!translations) { return undefined; }
  if (Array.isArray(translations)) {
    if (translations.length === 0) { return undefined; }
    const ru = translations.find((t) => t.languageCode === 'ru');
    return translationText(ru) ?? translationText(translations[0]);
  }
  // Record-формат: { ru: {...}, en: {...} }
  return (
    translationText(translations['ru']) ??
    translationText(Object.values(translations)[0])
  );
}

/**
 * Человекочитаемая подпись товара. Приоритет: русский перевод (title|name) →
 * любой перевод → плоское title/name → code (абонементы) → slug → #id.
 */
function entityLabel(e: MinimalEntity): string {
  const fromTranslations = textFromTranslations(e.translations);
  if (fromTranslations) { return fromTranslations; }
  if (typeof e.title === 'string' && e.title.length > 0) { return e.title; }
  if (typeof e.name === 'string' && e.name.length > 0) { return e.name; }
  if (typeof e.code === 'string' && e.code.length > 0) { return e.code; }
  if (typeof e.slug === 'string' && e.slug.length > 0) { return e.slug; }
  return `#${e.id}`;
}

function UserSelector({
  value,
  onChange,
}: {
  value: number | null;
  onChange: (u: MinimalUser | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const listResult = useList<MinimalUser>({
    resource: 'users',
    pagination: { currentPage: 1, pageSize: 50 },
    filters: search
      ? [{ field: 'search', operator: 'contains' as const, value: search }]
      : [],
  });
  const users = listResult.result.data ?? [];

  const selectedFromList = value !== null ? users.find((u) => u.id === value) ?? null : null;
  const oneResult = useOne<MinimalUser>({
    resource: 'users',
    id: value ?? '',
    queryOptions: { enabled: value !== null && !selectedFromList },
  });
  const selected = selectedFromList ?? oneResult.result ?? null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          aria-expanded={open}
          className="w-full justify-between"
        >
          <span className="truncate">
            {selected ? userLabel(selected) : 'Выберите пользователя...'}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Имя, username или email..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandList
            className="max-h-[260px] overscroll-contain"
            onWheel={(e) => e.stopPropagation()}
          >
            <CommandEmpty>Пользователи не найдены</CommandEmpty>
            <CommandGroup>
              {users.map((u) => (
                <CommandItem
                  key={u.id}
                  value={String(u.id)}
                  onSelect={() => {
                    onChange(u);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      value === u.id ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                  <div className="flex flex-1 flex-col">
                    <span className="truncate">{userLabel(u)}</span>
                    {u.email && (
                      <span className="text-xs text-muted-foreground">{u.email}</span>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function TargetSelector({
  resource,
  value,
  onChange,
}: {
  resource: string;
  value: number | null;
  onChange: (id: number | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const listResult = useList<MinimalEntity>({
    resource,
    pagination: { currentPage: 1, pageSize: 50 },
    filters: search
      ? [{ field: 'search', operator: 'contains' as const, value: search }]
      : [],
  });
  const entities = listResult.result.data ?? [];
  const selected = value !== null ? entities.find((e) => e.id === value) ?? null : null;

  let triggerLabel = 'Выберите товар...';
  if (selected) {
    triggerLabel = entityLabel(selected);
  } else if (value !== null) {
    triggerLabel = `#${value}`;
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          aria-expanded={open}
          className="w-full justify-between"
        >
          <span className="truncate">{triggerLabel}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Поиск товара..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandList
            className="max-h-[260px] overscroll-contain"
            onWheel={(e) => e.stopPropagation()}
          >
            <CommandEmpty>Ничего не найдено</CommandEmpty>
            <CommandGroup>
              {entities.map((e) => (
                <CommandItem
                  key={e.id}
                  value={String(e.id)}
                  onSelect={() => {
                    onChange(e.id);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      value === e.id ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                  <span className="truncate">{entityLabel(e)}</span>
                  <Badge variant="outline" className="ml-2 text-xs">
                    #{e.id}
                  </Badge>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Селектор тарифа товара. Грузит активные тарифы выбранного товара
 * через GET /pricing-tiers/admin?<tierFilter>=<targetId>&isActive=true.
 * Тариф обязателен — это единственный source of truth по цене заказа.
 */
function TierSelector({
  tierFilter,
  targetId,
  value,
  onChange,
}: {
  tierFilter: string;
  targetId: number;
  value: number | null;
  onChange: (id: number | null) => void;
}) {
  const [tiers, setTiers] = useState<PricingTier[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiClient
      .get<PricingTier[]>(
        `/pricing-tiers/admin?${tierFilter}=${targetId}&isActive=true`,
      )
      .then((result) => {
        if (cancelled) { return; }
        setTiers(result);
      })
      .catch(() => {
        if (cancelled) { return; }
        setTiers([]);
      })
      .finally(() => {
        if (!cancelled) { setLoading(false); }
      });
    return () => {
      cancelled = true;
    };
  }, [tierFilter, targetId]);

  if (loading) {
    return (
      <div className="flex h-9 items-center px-3 text-sm text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        Загрузка тарифов...
      </div>
    );
  }

  if (tiers.length === 0) {
    return (
      <p className="text-xs text-destructive">
        У товара нет активных тарифов — продажа невозможна
      </p>
    );
  }

  const selectedIndex = tiers.findIndex((t) => t.id === value);
  const selectedTier = selectedIndex >= 0 ? tiers[selectedIndex] : null;

  return (
    <div className="space-y-2">
      <Select
        value={value !== null ? String(value) : ''}
        onValueChange={(v) => onChange(Number(v))}
      >
        <SelectTrigger>
          <SelectValue placeholder="Выберите тариф..." />
        </SelectTrigger>
        <SelectContent>
          {tiers.map((t, index) => (
            <SelectItem key={t.id} value={String(t.id)}>
              {tierLabel(t, index)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {selectedTier && (
        <div className="rounded-md border bg-muted/30">
          <PricingTierInfoContent
            tier={selectedTier}
            index={selectedIndex}
          />
        </div>
      )}
    </div>
  );
}

function emptyItem(): DraftItem {
  return {
    key: crypto.randomUUID(),
    targetType: 'COURSE',
    targetId: null,
    pricingTierId: null,
    quantity: 1,
  };
}

export function CreateOrderInvoiceDialog({
  open,
  onOpenChange,
  onCreated,
}: CreateOrderInvoiceDialogProps) {
  const [selectedUser, setSelectedUser] = useState<MinimalUser | null>(null);
  const [items, setItems] = useState<DraftItem[]>([emptyItem()]);

  const [createdOrder, setCreatedOrder] = useState<Order | null>(null);
  const [creating, setCreating] = useState(false);

  const [provider, setProvider] = useState<'multicard' | 'cloudpayments'>('multicard');
  const [delivery, setDelivery] = useState<InvoiceDelivery>('copy');
  const [invoiceSessionId, setInvoiceSessionId] = useState<number | null>(null);
  const [generating, setGenerating] = useState(false);
  const [paymentUrl, setPaymentUrl] = useState<string | null>(null);
  const [deliveryNote, setDeliveryNote] = useState<string | null>(null);

  const resetAll = () => {
    setSelectedUser(null);
    setItems([emptyItem()]);
    setCreatedOrder(null);
    setCreating(false);
    setProvider('multicard');
    setDelivery('copy');
    setGenerating(false);
    setPaymentUrl(null);
    setDeliveryNote(null);
  };

  const handleClose = (next: boolean) => {
    if (!next) { resetAll(); }
    onOpenChange(next);
  };

  const canCreate = useMemo(() => {
    if (!selectedUser) { return false; }
    if (items.length === 0) { return false; }
    return items.every(
      (it) => it.targetId !== null && it.pricingTierId !== null && it.quantity > 0,
    );
  }, [selectedUser, items]);

  const updateItem = (key: string, patch: Partial<DraftItem>) => {
    setItems((prev) => prev.map((it) => (it.key === key ? { ...it, ...patch } : it)));
  };

  const handleCreateOrder = async () => {
    if (!selectedUser) { return; }
    // Идемпотентность на клиенте: не отправлять повторный запрос, если создание
    // уже идёт или заказ уже создан в этой сессии диалога (двойной клик / повтор).
    if (creating || createdOrder) { return; }
    setCreating(true);
    try {
      const payload = {
        userId: selectedUser.id,
        items: items.map((it) => ({
          targetType: it.targetType,
          targetId: it.targetId as number,
          pricingTierId: it.pricingTierId as number,
          quantity: it.quantity,
        })),
      };
      const order = await apiClient.post<Order>('/orders', payload);
      setCreatedOrder(order);
      notify.success(`Заказ №${order.id} создан`);
      onCreated?.();
    } catch (error) {
      notify.error(error instanceof Error ? error.message : 'Не удалось создать заказ');
    } finally {
      setCreating(false);
    }
  };

  const handleGenerateInvoice = async () => {
    if (!createdOrder) { return; }
    setGenerating(true);
    setPaymentUrl(null);
    setDeliveryNote(null);
    try {
      const result = await apiClient.post<{
        paymentUrl: string;
        delivered: boolean;
        deliveryError: string | null;
      }>(`/orders/${createdOrder.id}/invoice`, {
        provider,
        delivery,
        ...(delivery === 'telegram_session' && invoiceSessionId !== null
          ? { sessionId: invoiceSessionId }
          : {}),
      });
      setPaymentUrl(result.paymentUrl);
      if (delivery === 'copy') {
        notify.success('Ссылка на оплату создана');
      } else if (result.delivered) {
        const channelLabel =
          delivery === 'telegram'
            ? 'в Telegram'
            : delivery === 'telegram_session'
              ? 'через Telegram-сессию'
              : 'на email';
        notify.success(`Ссылка отправлена ${channelLabel}`);
      } else {
        setDeliveryNote(result.deliveryError ?? 'Не удалось доставить ссылку');
        notify.error(result.deliveryError ?? 'Ссылка создана, но не доставлена');
      }
    } catch (error) {
      notify.error(error instanceof Error ? error.message : 'Не удалось создать ссылку');
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = async () => {
    if (!paymentUrl) { return; }
    try {
      await navigator.clipboard.writeText(paymentUrl);
      notify.success('Скопировано в буфер обмена');
    } catch {
      notify.error('Не удалось скопировать');
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Создать заказ и инвойс</DialogTitle>
          <DialogDescription>
            Создайте заказ пользователю и сгенерируйте ссылку на оплату. После
            оплаты доступ выдаётся автоматически.
          </DialogDescription>
        </DialogHeader>

        {!createdOrder ? (
          <div className="space-y-5">
            <div className="space-y-2">
              <Label>Пользователь</Label>
              <UserSelector
                value={selectedUser?.id ?? null}
                onChange={setSelectedUser}
              />
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Позиции заказа</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setItems((prev) => [...prev, emptyItem()])}
                >
                  <Plus className="mr-1 h-4 w-4" />
                  Добавить
                </Button>
              </div>

              {items.map((it) => {
                const targetDef =
                  TARGET_TYPES.find((t) => t.value === it.targetType) ??
                  TARGET_TYPES[0];
                const resource = targetDef.resource;
                return (
                  <div
                    key={it.key}
                    className="space-y-3 rounded-md border p-3"
                  >
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Тип</Label>
                        <Select
                          value={it.targetType}
                          onValueChange={(v) =>
                            updateItem(it.key, {
                              targetType: v as TargetTypeValue,
                              targetId: null,
                              pricingTierId: null,
                            })
                          }
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {TARGET_TYPES.map((t) => (
                              <SelectItem key={t.value} value={t.value}>
                                {t.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Товар</Label>
                        <TargetSelector
                          resource={resource}
                          value={it.targetId}
                          onChange={(id) =>
                            updateItem(it.key, {
                              targetId: id,
                              pricingTierId: null,
                            })
                          }
                        />
                      </div>
                    </div>
                    {it.targetId !== null && (
                      <div className="space-y-1">
                        <Label className="text-xs">Тариф</Label>
                        <TierSelector
                          tierFilter={targetDef.tierFilter}
                          targetId={it.targetId}
                          value={it.pricingTierId}
                          onChange={(id) =>
                            updateItem(it.key, { pricingTierId: id })
                          }
                        />
                      </div>
                    )}
                    {items.length > 1 && (
                      <div className="flex justify-end">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-destructive"
                          onClick={() =>
                            setItems((prev) =>
                              prev.filter((x) => x.key !== it.key),
                            )
                          }
                        >
                          <Trash2 className="mr-1 h-4 w-4" />
                          Удалить позицию
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => handleClose(false)}
              >
                Отмена
              </Button>
              <Button disabled={!canCreate || creating} onClick={handleCreateOrder}>
                {creating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Создать заказ
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-5">
            <div className="rounded-md border bg-muted/30 p-4">
              <p className="text-sm text-muted-foreground">
                Заказ №{createdOrder.id} — {userLabel(createdOrder.user)}
              </p>
              <div className="mt-3 space-y-1">
                {createdOrder.items.map((item) => (
                  <div
                    key={item.id}
                    className="flex justify-between text-sm"
                  >
                    <span className="truncate">
                      {item.target?.name ?? `#${item.targetId}`} × {item.quantity}
                    </span>
                    <span className="tabular-nums">
                      {item.total} {createdOrder.currency}
                    </span>
                  </div>
                ))}
              </div>
              <div className="mt-3 flex justify-between border-t pt-3 text-base font-semibold">
                <span>Итого</span>
                <span className="tabular-nums">
                  {createdOrder.total} {createdOrder.currency}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Платёжный метод</Label>
                <Select
                  value={provider}
                  onValueChange={(v) =>
                    setProvider(v as 'multicard' | 'cloudpayments')
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="multicard">Rahmat (Multicard)</SelectItem>
                    <SelectItem value="cloudpayments">CloudPayments</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Способ отправки</Label>
                <Select
                  value={delivery}
                  onValueChange={(v) => {
                    if (isInvoiceDelivery(v)) {
                      setDelivery(v);
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="copy">Скопировать ссылку</SelectItem>
                    <SelectItem value="telegram">Отправить в Telegram (бот)</SelectItem>
                    <SelectItem value="telegram_session">
                      Отправить через Telegram-сессию
                    </SelectItem>
                    <SelectItem value="email">Отправить на email</SelectItem>
                  </SelectContent>
                </Select>
                {delivery === 'telegram_session' && (
                  <div className="mt-2 space-y-1">
                    <Label className="text-xs">Telegram-сессия</Label>
                    <SessionSelector
                      value={invoiceSessionId}
                      onChange={setInvoiceSessionId}
                      enabled={delivery === 'telegram_session'}
                    />
                  </div>
                )}
              </div>
            </div>

            {paymentUrl && (
              <div className="space-y-2">
                <Label className="text-xs">Ссылка на оплату</Label>
                <div className="flex gap-2">
                  <Input readOnly value={paymentUrl} className="font-mono text-xs" />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={handleCopy}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
                {deliveryNote && (
                  <p className="text-xs text-destructive">{deliveryNote}</p>
                )}
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => handleClose(false)}>
                Закрыть
              </Button>
              <Button disabled={generating} onClick={handleGenerateInvoice}>
                {generating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {paymentUrl ? 'Создать заново' : 'Создать ссылку'}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
